import React, { useEffect, useState } from 'react';
import Modal from '../../UI/Modal';
import Button from '../../UI/Button';
import Input from '../../UI/Input';
import '../BulkEditModal/BulkEditModal.css';
import './BulkStockModal.css';

const BulkStockModal = ({
  isOpen,
  onClose,
  selectedProducts,
  onSave,
  loading = false
}) => {
  const [rows, setRows] = useState([]);
  const [bulkAmount, setBulkAmount] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setRows([]);
      setBulkAmount('');
      return;
    }

    setRows(
      selectedProducts.map((product) => {
        const variant = product.variants?.[0];
        const trackingOn = Boolean(variant?.inventory_management);
        const current =
          variant?.inventory_quantity == null
            ? null
            : Number(variant.inventory_quantity);
        return {
          id: product.id,
          title: product.title,
          trackingOn,
          inventoryItemId: variant?.inventory_item_id || null,
          variantId: variant?.id || null,
          currentQty: Number.isInteger(current) ? current : 0,
          quantity: Number.isInteger(current) ? current : 0
        };
      })
    );
    setBulkAmount('');
  }, [isOpen, selectedProducts]);

  const parseBulkAmount = () => {
    const n = Number(bulkAmount);
    if (!Number.isInteger(n) || n < 0) return null;
    return n;
  };

  const applyToTracked = (computeNext) => {
    setRows((prev) =>
      prev.map((row) => {
        if (!row.trackingOn) return row;
        return { ...row, quantity: computeNext(row.quantity) };
      })
    );
  };

  const handleSetAll = () => {
    const n = parseBulkAmount();
    if (n == null) return;
    applyToTracked(() => n);
  };

  const handleAddAll = () => {
    const n = parseBulkAmount();
    if (n == null) return;
    applyToTracked((qty) => qty + n);
  };

  const handleSubtractAll = () => {
    const n = parseBulkAmount();
    if (n == null) return;
    applyToTracked((qty) => Math.max(0, qty - n));
  };

  const handleRowChange = (id, value) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    const raw = digits === '' ? '' : Number(digits);
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, quantity: raw } : row))
    );
  };

  const trackedRows = rows.filter((row) => row.trackingOn);
  const validRows = trackedRows.filter(
    (row) => Number.isInteger(row.quantity) && row.quantity >= 0
  );
  const canSave = validRows.length > 0 && validRows.length === trackedRows.length;

  const handleSave = () => {
    if (!canSave) return;
    onSave(
      validRows.map((row) => ({
        id: row.id,
        quantity: row.quantity,
        inventoryItemId: row.inventoryItemId,
        variantId: row.variantId
      }))
    );
  };

  const handleClose = () => {
    setRows([]);
    setBulkAmount('');
    onClose();
  };

  const bulkReady = parseBulkAmount() != null;
  const hasTracked = trackedRows.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Toplu Stok Düzenleme (${selectedProducts.length} ürün)`}
      size="mdPlusWide"
    >
      <div className="bulk-edit-container">
        <div className="bulk-stock-apply">
          <Input
            label="Tüm seçili ürünlere uygula"
            type="text"
            inputMode="numeric"
            value={bulkAmount}
            onChange={(e) => setBulkAmount(e.target.value)}
            placeholder="Adet"
            disabled={!hasTracked || loading}
            autoFocus
            autoComplete="off"
          />
          <div className="bulk-stock-apply-buttons">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSetAll}
              disabled={!bulkReady || !hasTracked || loading}
            >
              Hepsini bu yap
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleAddAll}
              disabled={!bulkReady || !hasTracked || loading}
            >
              Hepsine ekle
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleSubtractAll}
              disabled={!bulkReady || !hasTracked || loading}
            >
              Hepsinden çıkar
            </Button>
          </div>
          <p className="bulk-stock-apply-hint">
            Çıkarma 0’ın altına düşmez. Değişiklikler kaydete basınca Shopify’a gider.
            Çok sayıda üründe kayıt 30–60 sn sürebilir.
          </p>
        </div>

        <div className="bulk-edit-list">
          {rows.map((row) => (
            <div key={row.id} className="bulk-edit-item bulk-stock-item">
              <div className="product-info">
                <span className="product-title">{row.title}</span>
                <span className="bulk-current-price">
                  {row.trackingOn
                    ? `Mevcut stok: ${row.currentQty}`
                    : 'Stok takibi kapalı'}
                </span>
              </div>
              {row.trackingOn ? (
                <Input
                  label="Yeni stok"
                  type="text"
                  inputMode="numeric"
                  value={row.quantity === '' ? '' : String(row.quantity)}
                  onChange={(e) => handleRowChange(row.id, e.target.value)}
                  placeholder=""
                  disabled={loading}
                  autoComplete="off"
                />
              ) : (
                <p className="bulk-stock-untracked">
                  Bu ürün atlanır. Shopify’da Envanter → Miktarı izle.
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="bulk-actions">
          <Button
            variant="success"
            onClick={handleSave}
            loading={loading}
            disabled={!canSave || loading}
          >
            {loading ? 'Güncelleniyor...' : 'Tümünü Kaydet'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            İptal
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkStockModal;
